import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthProvider } from './contexts/AuthContext';
import { useAuth } from './hooks/useAuth';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import MainApp from './pages/MainApp';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import PlatformAccessDeclaration from './pages/PlatformAccessDeclaration';
import CookiePolicy from './pages/CookiePolicy';
import Impresszum from './pages/Impresszum';
import CookieBanner from './components/CookieBanner';
import LanguageSelector from './components/layout/LanguageSelector';
import tiktokAuth from './services/tiktokAuth';
import './App.css';

function AppContent() {
  const { user, loading } = useAuth();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [tiktokCallbackHandled, setTiktokCallbackHandled] = useState(false);

  useEffect(() => {
    document.title = t('common.pageTitle');
  }, [t, i18n.language]);

  const showFloatingLanguageSelector = !user;

  // Handle TikTok OAuth callback on /login route before redirect
  useEffect(() => {
    const handleTikTokOnLogin = async () => {
      if (location.pathname === '/login' && user && !tiktokCallbackHandled) {
        const urlParams = new URLSearchParams(location.search);
        const code = urlParams.get('code');
        const state = urlParams.get('state');
        const scopes = urlParams.get('scopes');
        
        // Check if this is a TikTok callback
        if (code && state && scopes && scopes.includes('user.info.basic')) {
          console.log('TikTok callback detected on /login, handling before redirect...');
          setTiktokCallbackHandled(true);
          await tiktokAuth.handleTikTokCallback();
          // Navigate to home after handling callback
          navigate('/', { replace: true });
          return;
        }
      }
    };
    handleTikTokOnLogin();
  }, [location, user, navigate, tiktokCallbackHandled]);

  // Check if we're on /login with TikTok callback params - don't redirect yet
  const isTikTokCallback = () => {
    if (location.pathname !== '/login') return false;
    const urlParams = new URLSearchParams(location.search);
    const code = urlParams.get('code');
    const scopes = urlParams.get('scopes');
    return code && scopes && scopes.includes('user.info.basic');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        {showFloatingLanguageSelector && (
          <div className="fixed bottom-4 right-4 z-[60] rounded-xl bg-white/90 p-3 shadow-lg backdrop-blur md:bottom-6 md:right-6">
            <LanguageSelector compact />
          </div>
        )}
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {showFloatingLanguageSelector && (
        <div className="fixed bottom-4 right-4 z-[60] rounded-xl bg-white/90 p-3 shadow-lg backdrop-blur md:bottom-6 md:right-6">
          <LanguageSelector compact />
        </div>
      )}
      <Routes>
        <Route path="/" element={user ? <MainApp /> : <LandingPage />} />
        <Route path="/login" element={
          user && !isTikTokCallback() ? <Navigate to="/" replace /> : <LoginPage />
        } />
        <Route path="/app" element={user ? <MainApp /> : <LandingPage />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/platform-access" element={<PlatformAccessDeclaration />} />
        <Route path="/cookie-policy" element={<CookiePolicy />} />
        <Route path="/impresszum" element={<Impresszum />} />
      </Routes>
    </>
  );
}

function App() {
  const [showCookieBanner, setShowCookieBanner] = useState(false);

  useEffect(() => {
    // Check if user has cookie consent
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      setShowCookieBanner(true);
    }
  }, []);

  const handleReopenCookieBanner = () => {
    setShowCookieBanner(true);
  };

  // Make handleReopenCookieBanner available globally
  useEffect(() => {
    window.reopenCookieBanner = handleReopenCookieBanner;
    return () => {
      delete window.reopenCookieBanner;
    };
  }, []);

  return (
    <Router>
      <AuthProvider>
        <AppContent />
        {showCookieBanner && <CookieBanner onClose={() => setShowCookieBanner(false)} />}
      </AuthProvider>
    </Router>
  );
}

export default App;